// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import { IconCloseTablet } from 'components/atoms/icons/IconCloseTablet';
import { Modal } from 'components/atoms/Modal';
import { useTranslation } from 'react-i18next';
import styles from './InputResourcesScanModal.module.scss';
import Html5QrcodePlugin from '../Html5QrcodePlugin';
import { Html5QrcodeScanType, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import InputResourcesScannedTable from '../InputResourcesScannedTable';
import { useEffect, useMemo, useState } from 'react';
import { InputResourceQRData, PreRegisSelectedResource } from 'util/Types';
import { OperatorType } from 'util/Enums';
import { handleError } from 'util/errorHandler';
import { createAxios } from 'ts/createAxios';
import { useSelector } from 'react-redux';
import { selectLoading } from 'redux/slices/loadingSlice';
import { normalizeNumber } from 'util/commons';
import BigNumber from 'bignumber.js';
import { toast } from 'react-toastify';

type InputResourcesScanModalProps = {
  operatorType: OperatorType;
  onClose?: () => void;
  onNext?: (values: PreRegisSelectedResource[]) => void;
};

const InputResourcesScanModal: React.FC<InputResourcesScanModalProps> = ({
  onClose,
  operatorType,
  onNext,
}) => {
  const { t } = useTranslation();
  const [scannedData, setScannedData] = useState<
    Record<string, InputResourceQRData>
  >({});
  const [scannedCode, setScannedCode] = useState<string>();
  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  const { isLoading } = useSelector(selectLoading);

  const resources = useMemo(
    () =>
      Object.values(scannedData)
        .map(({ resources, ...rest }) =>
          resources.map((item) => ({ ...item, ...rest }))
        )
        .flat(),
    [scannedData]
  );

  const totalWeight = useMemo(
    () =>
      resources.reduce(
        (acc, item) => BigNumber(item.remainingWeight).plus(acc),
        BigNumber(0)
      ),
    [resources]
  );

  const totalSelectedWeight = useMemo(
    () =>
      resources.reduce(
        (acc, item) =>
          checkedIds.includes(item.processingObjectId)
            ? BigNumber(item.remainingWeight).plus(acc)
            : acc,
        BigNumber(0)
      ),
    [resources, checkedIds]
  );

  const handleScanSuccess = (decodedText: string) => {
    if (
      decodedText &&
      (operatorType === OperatorType.Compressions ||
        operatorType === OperatorType.Recycle)
    ) {
      setScannedCode(decodedText);
    } else {
      setScannedCode(undefined);
    }
  };

  const getScannedData = async () => {
    if (!scannedCode || isLoading || scannedData[scannedCode]) {
      return;
    }

    try {
      const { data } = await createAxios().get<InputResourceQRData>(
        `/${operatorType.toLocaleLowerCase()}/input-resources/scan/${scannedCode}`
      );

      setScannedData((prev) => ({
        ...prev,
        [scannedCode]: data,
      }));
      setCheckedIds((prev) => [
        ...prev,
        ...data.resources.map((item) => item.processingObjectId),
      ]);
    } catch (error) {
      handleError(error);
    } finally {
      setScannedCode(undefined);
    }
  };

  const handleClickNext = () => {
    const selectedResources = resources.filter((item) =>
      checkedIds.includes(item.processingObjectId)
    );

    if (!selectedResources.length) {
      return;
    }

    const registeringData = selectedResources.map(
      ({ processingObjectId, remainingWeight, materialName }) => ({
        processingObjectId,
        weight: remainingWeight,
        materialName,
      })
    );

    if (
      registeringData.some(
        ({ materialName }) => materialName !== registeringData[0].materialName
      )
    ) {
      toast.error(t('collect_registration.submit_error_material_name'));

      return;
    }

    onNext?.(
      selectedResources.map(
        ({ processingObjectId, remainingWeight, materialName }) => ({
          processingObjectId,
          weight: remainingWeight,
          materialName,
        })
      )
    );
  };

  useEffect(() => {
    getScannedData();
  }, [scannedCode]);

  return (
    <Modal centered className={styles.scanModal}>
      <div className={styles.title}>
        <p>{t('pre_regist_resource_select.scan_qr')}</p>
        <IconCloseTablet onClick={onClose} />
      </div>

      <div className={styles.content}>
        <Html5QrcodePlugin
          fps={10}
          disableFlip={false}
          qrCodeSuccessCallback={handleScanSuccess}
          aspectRatio={1}
          formatToSupports={[Html5QrcodeSupportedFormats.QR_CODE]}
          supportedScanTypes={[Html5QrcodeScanType.SCAN_TYPE_CAMERA]}
          verbose={false}
          qrCodeRegionId="qrCode"
          className={styles.qrCodeRegion}
        />

        <div className={styles.scannedContent}>
          <div className={styles.scannedContentHeader}>
            <p className={styles.scannedTitle}>
              {t('pre_regist_resource_select.scan_modal.scanned_result')}
            </p>
            <p className={styles.scannedInfo}>
              {t('pre_regist_resource_select.scan_modal.scanned_total_items', {
                total: normalizeNumber({
                  value: resources.length,
                }),
              })}
            </p>
            <p className={styles.scannedInfo}>
              {t('pre_regist_resource_select.scan_modal.scanned_total_weight', {
                weight: normalizeNumber({ value: totalWeight }),
              })}
            </p>
          </div>

          <div className={styles.scannedTableWrapper}>
            <InputResourcesScannedTable
              data={scannedData}
              checkedIds={checkedIds}
              setCheckedIds={setCheckedIds}
            />

            <div className={styles.scannedFooter}>
              <p>
                {t(
                  'pre_regist_resource_select.scan_modal.total_selected_weight',
                  {
                    weight: normalizeNumber({
                      value: totalSelectedWeight,
                    }),
                  }
                )}
              </p>

              <button
                className={styles.btnRequest}
                type="button"
                disabled={!checkedIds.length}
                onClick={handleClickNext}
              >
                {t('pre_regist_resource_select.button_next')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default InputResourcesScanModal;
